import { useCallback } from 'react';
import { useError } from '../contexts/ErrorContext';
import { logger } from '../lib/logger';

interface ErrorHandlerOptions {
  context?: string;
  showToUser?: boolean;
  logLevel?: 'warn' | 'error' | 'critical';
}

export const useErrorHandler = () => {
  const { showError, showWarning } = useError();

  const handleError = useCallback((
    error: Error | string,
    options: ErrorHandlerOptions = {}
  ) => {
    const {
      context = 'Unknown',
      showToUser = true,
      logLevel = 'error'
    } = options;

    const errorMessage = typeof error === 'string' ? error : error.message;
    const errorStack = typeof error === 'string' ? undefined : error.stack;

    // Log the error
    switch (logLevel) {
      case 'warn':
        logger.warn(errorMessage, context, { stack: errorStack });
        break;
      case 'error':
        logger.error(errorMessage, context, { stack: errorStack });
        break;
      case 'critical':
        logger.critical(errorMessage, context, { stack: errorStack });
        break;
    }

    // Show to user if requested
    if (showToUser) {
      if (logLevel === 'warn') {
        showWarning(errorMessage);
      } else {
        showError(errorMessage);
      }
    }
  }, [showError, showWarning]);

  const handleAsyncError = useCallback(async <T>(
    asyncOperation: () => Promise<T>,
    options: ErrorHandlerOptions = {}
  ): Promise<T | null> => {
    try {
      return await asyncOperation();
    } catch (error) {
      handleError(error as Error, options);
      return null;
    }
  }, [handleError]);

  const handleSupabaseError = useCallback((
    error: any,
    operation: string,
    showToUser: boolean = true
  ) => {
    let userMessage = 'An unexpected error occurred. Please try again.';
    
    // Handle specific Supabase error codes
    if (error?.code) {
      switch (error.code) {
        case '23505': // Unique constraint violation
          userMessage = 'This item already exists. Please use different details.';
          break;
        case '23503': // Foreign key constraint violation
          userMessage = 'Cannot complete this action due to related data constraints.';
          break;
        case '42501': // Insufficient privilege
          userMessage = 'You do not have permission to perform this action.';
          break;
        case 'PGRST116': // No rows found
          userMessage = 'The requested item was not found.';
          break;
        case 'PGRST301': // Row level security violation
          userMessage = 'Access denied. Please check your permissions.';
          break;
        default:
          userMessage = error.message || userMessage;
      }
    }

    handleError(error, {
      context: `Supabase ${operation}`,
      showToUser,
      logLevel: 'error'
    });

    if (showToUser && userMessage !== error?.message) {
      showError(userMessage);
    }
  }, [handleError, showError]);

  return {
    handleError,
    handleAsyncError,
    handleSupabaseError
  };
};